import dayjs from 'dayjs';
import html2canvas from 'html2canvas';
import miniaturaDelivery from '@/assets/images/boas-vindas-delivery.jpg';
import miniaturaCardapio from '@/assets/images/boas-vindas-cardapio.jpg';

const STYLE_ID = 'bigou-banner-style';
const LADO_ALVO = 1080; // lado da imagem final (padrão de feed social)
const ESCALA_PADRAO = 2.7; // fallback para fragmentos sem largura declarada
const SVG_NS = 'http://www.w3.org/2000/svg';

export const ARTES_PROMOCAO = [
  { key: 'neon', nome: 'Opção 1' },
  { key: 'editorial', nome: 'Opção 2' },
];

export const ARTES_CUPOM = [
  { key: 'ticket', nome: 'Opção 1' },
  { key: 'bilhete', nome: 'Opção 2' },
];

const PERIODOS_USO = {
  1: { adjetivo: ['diário', 'diários'], curto: ['DIÁRIO', 'DIÁRIOS'], selo: 'POR DIA' },
  2: { adjetivo: ['mensal', 'mensais'], curto: ['MENSAL', 'MENSAIS'], selo: 'POR MÊS' },
};

// ── URLs de mídia ────────────────────────────────────────────────────────────
function urlUploads() {
  return process.env.VUE_APP_IMAGE_URL;
}

export function urlLogomarca(plano3) {
  return plano3 ? `${urlUploads()}/logomarca/${plano3}` : null;
}

export function urlItem(imagem) {
  return imagem ? `${urlUploads()}/item/${imagem}` : null;
}

// ── Formatação ───────────────────────────────────────────────────────────────

// Categoria principal da loja (o backend já ordena por `principal`).
function categoriaLoja(estabelecimento) {
  const categorias = estabelecimento.categorias;
  if (categorias && categorias.length && categorias[0].nome) {
    return categorias[0].nome;
  }
  if (estabelecimento.localidade) {
    return estabelecimento.localidade;
  }
  const localidades = estabelecimento.localidades;
  if (localidades && localidades.length && localidades[0].nome) {
    return localidades[0].nome;
  }
  return null;
}

function nomeECategoria(promo, estabelecimento) {
  let nome = String(promo.nome == null ? '' : promo.nome).trim();
  let categoria = promo.categoria || null;
  const separador = nome.indexOf(': ');

  if (!categoria && separador > 0) {
    categoria = nome.substring(0, separador);
    nome = nome.substring(separador + 2);
  }

  return {
    nome: nome,
    categoria: categoria || categoriaLoja(estabelecimento),
  };
}

function moeda(valor) {
  const numero = Number(valor);
  if (isNaN(numero)) {
    return '0,00';
  }
  return numero.toFixed(2).replace('.', ',');
}

// "R$10" lê melhor que "R$10,00" — os centavos só aparecem quando existem.
function valorDisplay(valor) {
  const numero = Number(valor) || 0;
  return `R$${numero % 1 === 0 ? String(numero) : moeda(numero)}`;
}

function semAcento(texto) {
  const valor = String(texto == null ? '' : texto);
  return valor.normalize ? valor.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : valor;
}

export function nomeArquivo(prefixo, identificador) {
  const limpo = semAcento(identificador)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 40);
  return `${prefixo}${limpo ? `-${limpo}` : ''}.png`;
}

function dataCurta(data) {
  if (!data) {
    return null;
  }
  const d = dayjs(data);
  return d.isValid() ? d.format('DD/MM') : null;
}

// ── Disponibilidade e público da promoção ────────────────────────────────────

const DIAS_PLURAL = ['domingos', 'segundas', 'terças', 'quartas', 'quintas', 'sextas', 'sábados'];
const DIAS_CURTOS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];

export const MOTIVO_SEGMENTADA = 'Promoção com público segmentado não gera imagem de divulgação.';

function diasDe(csv) {
  if (!csv) {
    return [];
  }
  const dias = String(csv)
    .split(',')
    .map((dia) => parseInt(dia, 10))
    .filter((dia) => !Number.isNaN(dia) && dia >= 0 && dia <= 6);
  return [...new Set(dias)].sort((a, b) => a - b);
}

function rotuloDias(csv) {
  const dias = diasDe(csv);
  if (!dias.length || dias.length === 7) {
    return null;
  }
  const chave = dias.join(',');
  if (chave === '1,2,3,4,5') {
    return 'só de segunda a sexta';
  }
  if (chave === '0,6') {
    return 'só nos fins de semana';
  }
  if (dias.length === 1) {
    const artigo = dias[0] === 0 || dias[0] === 6 ? 'aos' : 'às';
    return `só ${artigo} ${DIAS_PLURAL[dias[0]]}`;
  }
  const curtos = dias.map((dia) => DIAS_CURTOS[dia]);
  return `só ${curtos.slice(0, -1).join(', ')} e ${curtos[curtos.length - 1]}`;
}

// '11:00' → '11h'; '11:30' → '11h30'.
function horaCurta(hora) {
  const [inicio, minuto] = String(hora).trim().split(':');
  return minuto && minuto !== '00' ? `${inicio}h${minuto}` : `${inicio}h`;
}

// '11:00-14:00,18:00-22:00' → 'das 11h às 14h e das 18h às 22h'.
function rotuloHorarios(csv) {
  if (!csv) {
    return null;
  }
  const faixas = String(csv)
    .split(',')
    .map((faixa) => faixa.trim().split('-'))
    .filter((faixa) => faixa.length === 2 && faixa[0] && faixa[1])
    .map((faixa) => `das ${horaCurta(faixa[0])} às ${horaCurta(faixa[1])}`);
  return faixas.length ? faixas.join(' e ') : null;
}

// Disponibilidade restrita da promoção — a arte destaca esse texto.
export function disponibilidadeDe(promo) {
  const dias = rotuloDias(promo.disponibilidade_diaria);
  const horarios = rotuloHorarios(promo.horarios);
  if (dias && horarios) {
    return `${dias}, ${horarios}`;
  }
  if (dias) {
    return dias;
  }
  return horarios ? `só ${horarios}` : null;
}

// ── Rótulos curtos (a arte só tem uma linha para isso) ───────────────────────

// '1,2,3,4,5' → 'seg a sex'; '0,6' → 'sáb e dom'; '1' → 'segundas'.
function rotuloDiasCurto(csv) {
  const dias = diasDe(csv);
  if (!dias.length || dias.length === 7) {
    return null;
  }
  if (dias.join(',') === '0,6') {
    return 'sáb e dom';
  }
  if (dias.length === 1) {
    return DIAS_PLURAL[dias[0]];
  }
  const curtos = dias.map(dia => DIAS_CURTOS[dia]);
  const seguido = dias.every((dia, i) => i === 0 || dia === dias[i - 1] + 1);
  if (seguido && dias.length >= 3) {
    return `${curtos[0]} a ${curtos[curtos.length - 1]}`;
  }
  return `${curtos.slice(0, -1).join(', ')} e ${curtos[curtos.length - 1]}`;
}

// '11:00-14:00,18:00-22:00' → '11h-14h e 18h-22h'. Mais de duas faixas não
// cabem na etiqueta, então a terceira em diante fica só na legenda.
function rotuloHorariosCurto(csv) {
  if (!csv) {
    return null;
  }
  const faixas = String(csv).split(',')
    .map(faixa => faixa.trim().split('-'))
    .filter(faixa => faixa.length === 2 && faixa[0] && faixa[1])
    .map(faixa => `${horaCurta(faixa[0])}-${horaCurta(faixa[1])}`)
    .slice(0, 2);
  if (!faixas.length) {
    return null;
  }
  return faixas.length === 1 ? faixas[0] : faixas.join(' e ');
}

// Tamanho máximo do rótulo impresso na arte.
const DISPONIBILIDADE_MAX = 42;

// Versão da disponibilidade que vai impressa na arte — a legenda continua
// usando a frase longa de `disponibilidadeDe`.
export function disponibilidadeCurtaDe(promo) {
  const dias = rotuloDiasCurto(promo.disponibilidade_diaria);
  const horarios = rotuloHorariosCurto(promo.horarios);
  if (!dias || !horarios) {
    return dias || horarios;
  }
  const completo = `${dias}, ${horarios}`;
  // Acima disso a etiqueta da arte fica ilegível: sobra só a primeira faixa
  // de horário (a legenda continua trazendo todas).
  return completo.length <= DISPONIBILIDADE_MAX
    ? completo
    : `${dias}, ${horarios.split(' e ')[0]}`;
}

function temValor(valor) {
  if (valor == null || valor === 'NULL') {
    return false;
  }
  if (Array.isArray(valor)) {
    return valor.length > 0;
  }
  return String(valor).trim() !== '';
}

export function promocaoSegmentada(promo) {
  return !!promo && (temValor(promo.publico_id) || temValor(promo.publico_nomes));
}

// ── Payloads ─────────────────────────────────────────────────────────────────

function regraUsoCliente(cupom) {
  const quantidade = Number(cupom.quantidade_usuario);
  if (!quantidade || quantidade < 1) {
    return null;
  }
  const plural = quantidade > 1;
  const periodo = PERIODOS_USO[Number(cupom.tipo_quantidade_usuario)] || null;
  const usos = `${quantidade}${plural ? ' usos' : ' uso'}`;
  const indice = plural ? 1 : 0;

  return {
    label: `${usos}${periodo ? ` ${periodo.adjetivo[indice]}` : ''} por cliente`,
    curto: usos.toUpperCase() + (periodo ? ` ${periodo.curto[indice]}` : ' POR CLIENTE'),
    quantidade: usos.toUpperCase(),
    limite: quantidade,
    periodo: periodo ? periodo.selo : 'POR CLIENTE',
  };
}

// Cupom → arte quadrada 1080x1080 tipo "coupon", layout "ticket" ou "bilhete".
// O payload apenas declara quais regras existem; a arte decide como desenhar.
export function payloadCupom(cupom, estabelecimento, layout) {
  const entregaGratis = Number(cupom.entrega_gratis) === 1 || Number(cupom.taxa_entrega) === 1;
  let headline;
  let headlineSuffix;
  let subheadline;

  if (entregaGratis) {
    headline = 'GRÁTIS';
    headlineSuffix = '';
    subheadline = 'na taxa de entrega';
  } else if (Number(cupom.tipo_desconto) === 2) {
    headline = `${Math.round(Number(cupom.valor_desconto) * 100)}%`;
    headlineSuffix = 'OFF';
    subheadline = 'de desconto no seu pedido';
  } else {
    headline = valorDisplay(cupom.valor_desconto);
    headlineSuffix = '';
    subheadline = 'de desconto no seu pedido';
  }

  // O escopo muda a frase da arte: primeiro pedido na loja ou no app.
  let primeiroPedido = null;
  if (Number(cupom.primeiro_pedido_estabelecimento) === 1) {
    primeiroPedido = 'loja';
  } else if (Number(cupom.primeiro_pedido) === 1) {
    primeiroPedido = 'app';
  }

  const uso = regraUsoCliente(cupom);

  return {
    type: 'coupon',
    layout: layout || ARTES_CUPOM[0].key,
    storeName: estabelecimento.nome,
    storeCategory: categoriaLoja(estabelecimento),
    logoSrc: urlLogomarca(estabelecimento.plano3),
    initial: estabelecimento.nome,
    couponCode: cupom.codigo,
    headline: headline,
    headlineSuffix: headlineSuffix,
    subheadline: subheadline,
    minOrder: Number(cupom.valor_minimo) > 0 ? `R$ ${moeda(cupom.valor_minimo)}` : null,
    maxDiscount: Number(cupom.valor_desconto_maximo) > 0 ? `R$ ${moeda(cupom.valor_desconto_maximo)}` : null,
    discountTarget: entregaGratis ? 'delivery' : 'items',
    firstOrder: primeiroPedido,
    usageLabel: uso ? uso.label : null,
    usageShort: uso ? uso.curto : null,
    usageAmount: uso ? uso.quantidade : null,
    usagePeriod: uso ? uso.periodo : null,
    usageLimit: uso ? uso.limite : null,
    validUntil: dataCurta(cupom.data_fim),
    ctaLabel: 'Peça no app',
  };
}

// Promoção → arte quadrada 1080x1080 tipo "feed", layout "neon" ou "editorial".
export function payloadPromocao(promo, estabelecimento, layout) {
  const preco = Number(promo.preco) || 0;
  const precoPromocional = Number(promo.preco_promocional) || 0;
  const economia = preco - precoPromocional;
  const percentual = preco > 0 ? Math.round((economia / preco) * 100) : 0;
  const item = nomeECategoria(promo, estabelecimento);

  return {
    type: 'feed',
    layout: layout || ARTES_PROMOCAO[0].key,
    storeName: estabelecimento.nome,
    logoSrc: urlLogomarca(estabelecimento.plano3),
    initial: estabelecimento.nome,
    productName: item.nome,
    productCategory: item.categoria,
    // A arte sempre exibe uma imagem: a do item ou, na falta dela, a logo da
    // loja — a troca acontece no gerador.
    imageSrc: urlItem(promo.imagem),
    originalPrice: moeda(preco),
    promoPrice: moeda(precoPromocional),
    savings: economia > 0 ? moeda(economia) : null,
    discount: percentual,
    availability: disponibilidadeCurtaDe(promo),
    validUntil: dataCurta(promo.data_fim),
    ctaLabel: 'Peça no app',
  };
}

export function layoutBoasVindas(estabelecimento) {
  return estabelecimento && Number(estabelecimento.cardapio_digital) === 1 ? 'cardapio' : 'delivery';
}

export function artesBoasVindas(estabelecimento) {
  const layout = layoutBoasVindas(estabelecimento);
  return [
    { key: layout, nome: 'Opção 1' },
    { key: `${layout}-celular`, nome: 'Opção 2' },
  ];
}

export const FORMATOS_BOAS_VINDAS = [
  { key: '4:5', nome: 'Feed (4:5)', sufixo: 'feed', dica: 'Imagem de 1080×1350 (4:5), no tamanho do feed.' },
  { key: '9:16', nome: 'Stories (9:16)', sufixo: 'stories', dica: 'Imagem de 1080×1920 (9:16), no tamanho dos stories e do status do WhatsApp.' },
];

export function miniaturaBoasVindas(estabelecimento) {
  return layoutBoasVindas(estabelecimento) === 'cardapio' ? miniaturaCardapio : miniaturaDelivery;
}

export const AVISO_LOJA_NAO_ATIVADA = 'Publique depois que a loja for ativada. Antes disso, os clientes ainda não conseguem fazer pedidos.';

function categoriaPrincipal(estabelecimento) {
  const categorias = estabelecimento.categorias;
  if (categorias && categorias.length && categorias[0].nome) {
    return categorias[0].nome;
  }
  return null;
}

export function payloadBoasVindas(estabelecimento, layout, ratio) {
  return {
    type: 'welcome',
    layout: layout || layoutBoasVindas(estabelecimento),
    ratio: ratio || FORMATOS_BOAS_VINDAS[0].key,
    storeName: estabelecimento.nome,
    storeCategory: categoriaPrincipal(estabelecimento),
    logoSrc: urlLogomarca(estabelecimento.plano3),
    initial: estabelecimento.nome,
  };
}

// ── Legendas prontas para WhatsApp / redes ───────────────────────────────────

export function legendaCupom(cupom, estabelecimento) {
  const payload = payloadCupom(cupom, estabelecimento);
  const regras = [];

  regras.push(payload.minOrder ? `Pedido mínimo de ${payload.minOrder}` : 'Sem pedido mínimo');
  if (payload.maxDiscount) {
    regras.push(`Desconto máximo de ${payload.maxDiscount}`);
  }
  if (payload.firstOrder) {
    regras.push(`Válido só no 1º pedido ${payload.firstOrder === 'app' ? 'no app' : 'na loja'}`);
  }
  if (payload.usageLabel) {
    regras.push(payload.usageLabel);
  }

  return `🎟️ Cupom exclusivo na ${estabelecimento.nome}!`
    + `\n\n${payload.headline}${payload.headlineSuffix ? ` ${payload.headlineSuffix}` : ''} ${payload.subheadline}.`
    + `\n\nUse o código *${String(cupom.codigo || '').toUpperCase()}* no app do Bigou.`
    + (payload.validUntil ? `\nVálido até ${payload.validUntil}.` : '')
    + `\n\n${regras.join('\n')}`;
}

export function legendaPromocao(promo, estabelecimento) {
  const payload = payloadPromocao(promo, estabelecimento);
  const disponibilidade = disponibilidadeDe(promo);
  return `🔥 Promoção na ${estabelecimento.nome}!`
    + `\n\n${payload.productName}`
    + `\nDe R$ ${payload.originalPrice} por *R$ ${payload.promoPrice}* (${payload.discount}% OFF)`
    + (disponibilidade ? `\n\n⏰ Válido ${disponibilidade}.` : '')
    + '\n\nPeça agora no app do Bigou.';
}

export function legendaBoasVindas(estabelecimento, link) {
  if (layoutBoasVindas(estabelecimento) === 'cardapio') {
    return '🎉 Conheça nosso cardápio digital!'
      + '\n\nVeja o cardápio completo e faça seu pedido direto com a gente, pelo celular, sem baixar nenhum app.'
      + (link ? `\n\nPeça pelo link:\n${link}` : '');
  }
  return '🎉 Estamos no Bigou!'
    + '\n\nAgora você pode pedir com a gente pelo app do Bigou Delivery e receber aí na sua casa.'
    + `\n\nProcure por *${estabelecimento.nome}* no app`
    + (link ? ` ou faça seu pedido pelo link:\n${link}` : '.');
}

// ── Fragmento da API ─────────────────────────────────────────────────────────

async function fetchFragment(axios, payload) {
  let response;

  try {
    response = await axios.post('/banner-html/generate', { format: 'json', ...payload });
  } catch (error) {
    const err = error.response && error.response.data ? error.response.data.error : null;
    throw new Error(err || 'Erro ao gerar o banner');
  }

  if (!response || !response.data || !response.data.html) {
    throw new Error('Resposta inválida do gerador de banners');
  }

  return response.data;
}

// ── Renderização para PNG ────────────────────────────────────────────────────

function ensureAssets(fragment) {
  const head = document.getElementsByTagName('head')[0];

  let style = document.getElementById(STYLE_ID);
  if (!style) {
    style = document.createElement('style');
    style.id = STYLE_ID;
    head.appendChild(style);
  }
  if (style.innerHTML !== fragment.css) {
    style.innerHTML = fragment.css;
  }

  // Cada arte pede um conjunto próprio de famílias: injeta só o que faltar,
  // sem duplicar o que já está no <head>.
  (fragment.fonts || []).forEach(url => {
    if (document.querySelector(`link[data-bigou-font="${url}"]`)) {
      return;
    }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.setAttribute('data-bigou-font', url);
    link.href = url;
    head.appendChild(link);
  });
}

// As fontes precisam estar carregadas antes do html2canvas medir o texto, senão
// a arte é rasterizada com a fonte de fallback.
function fontesProntas() {
  return new Promise(resolve => {
    const pronto = () => setTimeout(resolve, 60);

    if (!document.fonts || !document.fonts.ready) {
      setTimeout(pronto, 400);
      return;
    }
    document.fonts.ready.then(pronto).catch(pronto);
  });
}

function protegerDoModoEscuro(doc, alvo) {
  if (!doc.querySelector('.darkreader, meta[name="darkreader"]')) {
    return;
  }

  Array.prototype.slice.call(doc.querySelectorAll('style.darkreader, link.darkreader, meta[name="darkreader"]'))
    .forEach(no => no.parentNode.removeChild(no));

  const arte = [alvo].concat(Array.prototype.slice.call(alvo.querySelectorAll('*')));
  const ancestrais = [];
  for (let no = alvo.parentElement; no; no = no.parentElement) {
    ancestrais.push(no);
  }

  arte.concat(ancestrais).forEach(el => {
    Array.prototype.slice.call(el.attributes)
      .filter(attr => attr.name.indexOf('data-darkreader') === 0)
      .forEach(attr => el.removeAttribute(attr.name));
    if (el.style) {
      Array.prototype.slice.call(el.style)
        .filter(prop => prop.indexOf('--darkreader') === 0)
        .forEach(prop => el.style.removeProperty(prop));
    }
  });

  arte.forEach(el => {
    if (!el.style) {
      return;
    }
    Array.prototype.slice.call(el.style)
      .forEach(prop => el.style.setProperty(prop, el.style.getPropertyValue(prop), 'important'));
    if (el.namespaceURI === SVG_NS) {
      ['fill', 'stroke'].forEach(attr => {
        el.style.setProperty(attr, el.getAttribute(attr) || 'inherit', 'important');
      });
    }
    if (!el.style.backgroundColor) {
      el.style.setProperty('background-color', 'transparent', 'important');
    }
    if (!el.style.color && el !== alvo) {
      el.style.setProperty('color', 'inherit', 'important');
    }
  });
}

// O fragmento declara a largura em que a arte foi desenhada; a escala é o
// quanto falta para chegar nos 1080px finais.
function escalaDe(fragment, escala) {
  if (escala) {
    return escala;
  }
  if (fragment.width) {
    return LADO_ALVO / fragment.width;
  }
  return ESCALA_PADRAO;
}

async function toPng(fragment, escala) {
  ensureAssets(fragment);

  const host = document.createElement('div');
  host.setAttribute('style', 'position:fixed;left:-10000px;top:0;z-index:-1;opacity:1;');
  host.innerHTML = fragment.html;
  document.body.appendChild(host);

  const limpar = () => {
    if (host.parentNode) {
      host.parentNode.removeChild(host);
    }
  };

  const alvo = host.querySelector('.banner');
  if (!alvo) {
    limpar();
    throw new Error('Banner não encontrado no fragmento');
  }

  try {
    await fontesProntas();
    const canvas = await html2canvas(alvo, {
      scale: escalaDe(fragment, escala),
      backgroundColor: null,
      useCORS: true,
      logging: false,
      width: alvo.offsetWidth,
      height: alvo.offsetHeight,
      onclone: protegerDoModoEscuro,
    });
    return canvas.toDataURL('image/png');
  } catch (err) {
    throw err instanceof Error ? err : new Error('Falha ao rasterizar a arte');
  } finally {
    limpar();
  }
}

// Gera o PNG da arte. `axios` é a instância do app (Vue.prototype.$axios), que
// já carrega baseURL, token e chave de API.
export async function gerar(axios, payload, escala) {
  const fragment = await fetchFragment(axios, payload);
  return toPng(fragment, escala);
}

// ── Saída: baixar / compartilhar / copiar ────────────────────────────────────

function dataUrlToBlob(dataUrl) {
  const partes = dataUrl.split(',');
  const mime = partes[0].match(/:(.*?);/)[1];
  const binario = atob(partes[1]);
  const buffer = new Uint8Array(binario.length);
  for (let i = 0; i < binario.length; i++) {
    buffer[i] = binario.charCodeAt(i);
  }
  return new Blob([buffer], { type: mime });
}

export function baixar(dataUrl, filename) {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = filename || 'bigou.png';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// Web Share só aceita arquivos em contexto seguro e em navegadores compatíveis.
// Fora disso, cai para o download.
export async function compartilhar(params) {
  let arquivo;

  try {
    arquivo = new File([dataUrlToBlob(params.dataUrl)], params.filename, { type: 'image/png' });
  } catch (err) {
    baixar(params.dataUrl, params.filename);
    return 'download';
  }

  if (!navigator.share || !navigator.canShare || !navigator.canShare({ files: [arquivo] })) {
    baixar(params.dataUrl, params.filename);
    return 'download';
  }

  try {
    await navigator.share({
      files: [arquivo],
      title: params.title || 'Bigou',
      text: params.text || '',
    });
    return 'share';
  } catch (err) {
    // Fechar a folha de compartilhamento não é erro.
    if (err && err.name === 'AbortError') {
      return 'cancelado';
    }
    throw err;
  }
}

export function copiar(dataUrl) {
  if (!navigator.clipboard || !window.ClipboardItem) {
    return Promise.reject(new Error('Este navegador não permite copiar imagens'));
  }

  try {
    const item = new window.ClipboardItem({ 'image/png': dataUrlToBlob(dataUrl) });
    return navigator.clipboard.write([item]);
  } catch (err) {
    return Promise.reject(err);
  }
}

export function copiarTexto(texto) {
  if (!navigator.clipboard) {
    return Promise.reject(new Error('Este navegador não permite copiar texto'));
  }
  return navigator.clipboard.writeText(texto);
}

export default {
  ARTES_CUPOM,
  ARTES_PROMOCAO,
  MOTIVO_SEGMENTADA,
  promocaoSegmentada,
  disponibilidadeDe,
  disponibilidadeCurtaDe,
  payloadCupom,
  payloadPromocao,
  payloadBoasVindas,
  layoutBoasVindas,
  artesBoasVindas,
  FORMATOS_BOAS_VINDAS,
  miniaturaBoasVindas,
  AVISO_LOJA_NAO_ATIVADA,
  legendaCupom,
  legendaPromocao,
  legendaBoasVindas,
  nomeArquivo,
  urlLogomarca,
  urlItem,
  gerar,
  baixar,
  compartilhar,
  copiar,
  copiarTexto,
};
